import { useEffect, useRef, useState } from 'react';
import { requestPasswordRecovery, resetPassword } from '../../services/api';
import { isValidUserEmail } from '../../utils/userEmail';

const getError = (error) => error.response?.data?.error || 'Não foi possível concluir. Tente novamente.';
const EMAIL_PENDING_MESSAGE = 'E-mail pendente no cadastro. Entre em contato com um gerente para cadastrar seu e-mail e recuperar a senha.';

export default function PasswordRecoveryForm({ initialEmail = '', onBack, onSuccess }) {
  const [email, setEmail] = useState(initialEmail);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialEmail.trim() && !initialEmail.includes('@') ? EMAIL_PENDING_MESSAGE : '');
  const [notice, setNotice] = useState('');
  const [remaining, setRemaining] = useState(0);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!remaining) return undefined;
    const timer = setTimeout(() => setRemaining((value) => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [remaining]);

  const sendCode = async () => {
    const value = email.trim();
    if (value && !value.includes('@')) {
      setError(EMAIL_PENDING_MESSAGE);
      return;
    }
    if (!isValidUserEmail(value)) {
      setError('Informe um e-mail válido.');
      return;
    }
    const { data } = await requestPasswordRecovery(value.toLowerCase());
    setChallenge(data.requestId);
    setCode('');
    setNotice(data.message);
    setRemaining(data.retryAfter || 60);
  };

  const run = async (action) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try { await action(); } catch (err) { setError(getError(err)); }
    finally { inFlight.current = false; setBusy(false); }
  };

  const submit = (event) => {
    event.preventDefault();
    run(async () => {
      if (!challenge) return sendCode();
      if (password.length < 6) {
        setError('A senha deve ter ao menos 6 caracteres.');
        return;
      }
      if (password !== confirmPassword) {
        setError('As senhas não conferem.');
        return;
      }
      const { data } = await resetPassword({ requestId: challenge, code, password, confirmPassword });
      setPassword('');
      setConfirmPassword('');
      setCode('');
      onSuccess(data.message);
    });
  };

  const changeEmail = () => {
    setChallenge(null);
    setCode('');
    setPassword('');
    setConfirmPassword('');
    setNotice('');
    setError('');
  };

  return (
    <form className="login-form password-recovery" onSubmit={submit} aria-busy={busy}>
      <p className="password-recovery__help">
        {challenge ? `Informe o código de 6 dígitos enviado para ${email} e escolha uma nova senha. O código vale por 10 minutos.`
          : 'Informe seu e-mail de acesso. Se você ainda usa um login sem e-mail, entre em contato com um gerente para regularizar seu cadastro.'}
      </p>
      {!challenge ? (
        <>
          <label className="input-label" htmlFor="recovery-email">E-mail ou login de acesso</label>
          <div className="login-input-wrap">
            <input id="recovery-email" type="text" inputMode="email" autoComplete="username" maxLength={254}
              value={email} onChange={(event) => { setEmail(event.target.value); setError(''); }} required disabled={busy} autoFocus />
          </div>
        </>
      ) : (
        <>
          <label className="input-label" htmlFor="recovery-code">Código de recuperação</label>
          <div className="login-input-wrap">
            <input id="recovery-code" type="text" inputMode="numeric" autoComplete="one-time-code"
              pattern="[0-9]{6}" maxLength={6} value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              required disabled={busy} autoFocus />
          </div>
          <label className="input-label" htmlFor="recovery-password">Nova senha</label>
          <div className="login-input-wrap">
            <input id="recovery-password" type="password" autoComplete="new-password" minLength={6}
              value={password} onChange={(event) => setPassword(event.target.value)}
              required disabled={busy} aria-describedby="recovery-password-help" />
          </div>
          <p id="recovery-password-help" className="password-recovery__help">A senha deve ter ao menos 6 caracteres.</p>
          <label className="input-label" htmlFor="recovery-confirm">Confirme a nova senha</label>
          <div className="login-input-wrap">
            <input id="recovery-confirm" type="password" autoComplete="new-password" minLength={6}
              value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} required disabled={busy} />
          </div>
        </>
      )}
      {notice && <p className="password-recovery__notice" role="status">{notice}</p>}
      {error && <p className="error-message" role="alert">{error}</p>}
      <button type="submit" className="login-button" disabled={busy}>
        {busy ? 'Aguarde…' : challenge ? 'Atualizar senha' : 'Enviar código'}
      </button>
      {challenge && (
        <div className="password-recovery__actions">
          <button type="button" className="login-text-button" disabled={busy || remaining > 0}
            onClick={() => run(sendCode)}>{remaining ? `Reenviar em ${remaining}s` : 'Reenviar código'}</button>
          <button type="button" className="login-text-button" disabled={busy} onClick={changeEmail}>Alterar e-mail</button>
        </div>
      )}
      <button type="button" className="login-text-button" disabled={busy} onClick={onBack}>Voltar para o login</button>
    </form>
  );
}
